import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import Icon from '@components/Icon';
import { supportedLanguages, isRtlLanguage } from '@i18n/config';

/**
 * 语言切换组件
 * 显示当前语言，支持下拉选择切换，带国旗图标和滚动支持
 */
const LanguageSwitcher = () => {
  const { i18n } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  // 添加自定义滚动条样式
  useEffect(() => {
    const styleId = 'language-switcher-scrollbar-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .language-switcher-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .language-switcher-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .language-switcher-scroll::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        .language-switcher-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.7);
        }
        .dark .language-switcher-scroll::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
        }
        .dark .language-switcher-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(75, 85, 99, 0.7);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    // 明确保存到 localStorage，确保语言设置持久化
    localStorage.setItem('i18nextLng', langCode);
    setVisible(false);
  };

  const currentLanguage = supportedLanguages.find(
    (lang) => lang.code === i18n.language || lang.code === i18n.language.split('-')[0]
  ) || supportedLanguages[0];

  const isRtl = isRtlLanguage(i18n.language);

  // 渲染所有语言项
  const renderLanguageItem = (lang) => (
    <div 
      key={lang.code}
      className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors"
      onClick={() => handleLanguageChange(lang.code)}
    >
      <div className="flex items-center space-x-2">
        <span className="text-lg">{lang.flag || '🌐'}</span>
        <span className="text-sm">{lang.nativeName}</span>
      </div>
      {(i18n.language === lang.code || i18n.language.split('-')[0] === lang.code) && (
        <Icon.Check size={16} className="text-purple-600 dark:text-purple-400" />
      )}
    </div>
  );

  // 自定义下拉菜单内容，一次性加载所有语言
  const dropdownContent = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div 
        className="max-h-[300px] overflow-y-auto language-switcher-scroll"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent',
        }}
      >
        {supportedLanguages.map(renderLanguageItem)}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <Button
        type="text"
        className="flex items-center space-x-2 px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-200 shadow-sm hover:shadow-md"
        title={currentLanguage.nativeName}
        onClick={() => setVisible(!visible)}
      >
        <span className="text-lg">{currentLanguage.flag || '🌐'}</span>
        <span className="hidden sm:inline text-sm font-medium">{currentLanguage.nativeName}</span>
        <Icon.ChevronDown 
          size={14} 
          className={`transition-transform duration-200 ${visible ? 'rotate-180' : ''}`}
        />
      </Button>
      
      {visible && (
        <>
          {/* 点击外部区域关闭下拉菜单 */}
          <div 
            className="fixed inset-0 z-[1000]"
            onClick={() => setVisible(false)}
          />
          {/* 下拉菜单 */}
          <div 
            className={`absolute top-full mt-2 z-[1001] ${isRtl ? 'left-0' : 'right-0'}`}
            style={{ minWidth: '200px' }}
          >
            {dropdownContent}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;

